import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelGuideObject from '@/entities/travelGuide/interface'

import {
  ALL_TEAMS_QUERY,
  SINGLE_TEAM_QUERY,
  TEAMS_DEPENDENTS_QUERY,
  CREATE_TEAM_MUTATION,
  UPDATE_TEAM_MUTATION,
  DELETE_TEAM_MUTATION,
  FORCE_DELETE_TEAM_MUTATION,
  ADD_TEAM_MEMBERS_MUTATION,
  REMOVE_TEAM_MEMBERS_MUTATION,
  CREATE_TEAM_MEMBERS_MUTATION,
  DELETE_TEAM_MEMBERS_MUTATION,
  UPDATE_TEAM_MEMBERS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travelTeamRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TEAM_QUERY, variables).then((data: any) => {
      return hydrateObject(data.team)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TEAMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.teams)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TEAM_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.team),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TEAMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.teams),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TEAMS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TEAMS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TEAM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.team)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TEAM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.team)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TEAM_MUTATION, variables).then((data: any) => {
      return data.deleteTravelTeam
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TEAM_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTravelTeam
    })
  },

  assignMembers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        memberIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TEAM_MEMBERS_MUTATION, variables)
  },

  unassignMembers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        memberIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TEAM_MEMBERS_MUTATION, variables)
  },

  createMembers: async (id: string, items: Array<travelGuideObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        members: items,
      },
    }
    return graphQLClient.request(CREATE_TEAM_MEMBERS_MUTATION, variables)
  },

  deleteMembers: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        memberIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TEAM_MEMBERS_MUTATION, variables)
  },

  updateMembersOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        memberIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TEAM_MEMBERS_ORDER_MUTATION, variables)
  },
}

export default travelTeamRepository
